import _extends from "@babel/runtime/helpers/extends";
import { Box, IconButton, Tooltip } from '@wix/design-system';
import React, { useEffect, useRef, useState } from 'react';
import { dataHooks } from '../../utils/datahooks';
import { resolveWdsBoxColorProps, wdsThemeColors } from '../../utils/wds-theme-colors';
export const ruleElementWidth = {
  full: '100%',
  large: '320px',
  normal: '260px',
  small: '252px'
};
const resolveHoverBackgroundProps = (hoveredBackgroundColor, isActive) => {
  if (!isActive || !hoveredBackgroundColor) {
    return {
      backgroundColor: 'transparent'
    };
  }
  const colorProps = resolveWdsBoxColorProps(hoveredBackgroundColor, 'backgroundColor');
  return {
    backgroundColor: colorProps.backgroundColor ?? 'transparent',
    style: colorProps.style
  };
};
export const HoverStyledBox = _ref => {
  let {
    notHoveredBorderColor = wdsThemeColors.borderStandard,
    hoveredBackgroundColor = 'B60',
    hoveredBorderColor = wdsThemeColors.borderFocus,
    iconButton,
    iconButtonTooltip,
    iconButtonPriority = 'secondary',
    iconButtonSkin,
    onClickIcon,
    iconButtonZIndex = 1,
    isShowIconButton = false,
    isOpen = false,
    onClick,
    children,
    dataHook,
    disabled = false,
    autoFocus = false,
    ariaExpanded,
    ariaLabel,
    ariaControls
  } = _ref;
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [initialFocus, setInitialFocus] = useState(autoFocus);
  const wrapperRef = useRef(null);
  useEffect(() => {
    if (!initialFocus) {
      return;
    }
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setInitialFocus(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [initialFocus]);
  const handleKeyDown = event => {
    if (disabled || isOpen) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick == null || onClick();
    }
  };
  const handleFocus = () => {
    if (disabled) {
      return;
    }
    setIsFocused(true);
  };
  const handleBlur = event => {
    if (disabled) {
      return;
    }
    // Check if focus is moving to a child element within the container
    if (wrapperRef.current && wrapperRef.current.contains(event.relatedTarget)) {
      return;
    }
    setIsFocused(false);
  };
  const handleMouseLeave = () => {
    if (disabled) {
      return;
    }
    setIsHovered(false);
    // Clear focus state when mouse leaves, unless element is still actively focused via keyboard
    // Check if the element is still the active element in the document
    if (wrapperRef.current && document.activeElement !== wrapperRef.current) {
      setIsFocused(false);
    }
  };
  const showIcon = Boolean(iconButton) && (isHovered || isFocused) && isShowIconButton;
  const borderColor = isOpen ? hoveredBorderColor : initialFocus ? wdsThemeColors.borderFocusStrong : isHovered || isFocused ? hoveredBorderColor : notHoveredBorderColor;
  const isHoverBackgroundActive = !isOpen && (isHovered || isFocused);
  const hoverBackgroundProps = resolveHoverBackgroundProps(hoveredBackgroundColor, isHoverBackgroundActive);
  return /*#__PURE__*/React.createElement("div", {
    "aria-controls": ariaControls,
    "aria-expanded": ariaExpanded,
    "aria-label": ariaLabel,
    "data-hook": dataHooks.a11yWizardHoverBoxContainerDiv(dataHook),
    role: "button",
    style: {
      borderColor,
      borderRadius: '6px',
      borderStyle: 'solid',
      borderWidth: '1px',
      cursor: isOpen || disabled ? 'default' : 'pointer',
      position: 'relative'
    },
    tabIndex: disabled || isOpen ? -1 : 0,
    onBlur: handleBlur,
    onClick: !isOpen && !disabled ? onClick : undefined,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onMouseEnter: () => !disabled && setIsHovered(true),
    onMouseLeave: handleMouseLeave
  }, /*#__PURE__*/React.createElement(Box, _extends({
    borderRadius: "6px",
    dataHook: dataHooks.a11yWizardHoverBoxContainer(dataHook),
    position: "relative",
    ref: wrapperRef,
    width: "100%"
  }, hoverBackgroundProps), showIcon && /*#__PURE__*/React.createElement(Box, {
    position: "absolute",
    right: "5%",
    style: {
      transform: 'translateY(-50%)'
    },
    top: "50%",
    zIndex: iconButtonZIndex
  }, /*#__PURE__*/React.createElement(Tooltip, {
    content: iconButtonTooltip,
    dataHook: dataHooks.a11yWizardHoverBoxIconButtonTooltip(dataHook),
    disabled: !iconButtonTooltip
  }, /*#__PURE__*/React.createElement(IconButton, {
    dataHook: dataHooks.a11yWizardHoverBoxIconButton(dataHook),
    priority: iconButtonPriority,
    size: "tiny",
    skin: iconButtonSkin,
    tabIndex: -1,
    onClick: e => {
      e.stopPropagation();
      !disabled && (onClickIcon == null ? void 0 : onClickIcon());
    }
  }, iconButton))), children));
};
//# sourceMappingURL=hover-styled-box.js.map